import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";
import { API_BASE_URL, apiFetch } from "../lib/api";
import { isPushEnabled } from "../lib/push";

/** Alerts bell — lives in the brand header; opens the alerts screen via event. */
function HeaderBell(): ReactNode {
  const q = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () =>
      apiFetch<{ notifications: unknown[] }>("/push/notifications?unread=1"),
    refetchInterval: 60_000,
  });
  // Whether lock-screen push actually reaches THIS device. Shared cache key with
  // the Alerts-tab setup card, so enabling there updates the bell immediately.
  const push = useQuery({
    queryKey: ["push-enabled"],
    queryFn: () => isPushEnabled(),
    refetchOnWindowFocus: true,
  });
  const unread = q.data?.notifications.length ?? 0;
  // A muted bell (slash + amber) tells a manager, from any screen, that push is
  // off on this device — so unseen alerts aren't mistaken for "nothing happening".
  const pushOff = push.data === false;
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent("tanzeel:alerts"))}
      aria-label={
        pushOff
          ? "Alerts — push notifications are off on this device"
          : unread > 0
            ? `Alerts, ${unread} unread`
            : "Alerts"
      }
      className="relative -m-1 p-1 text-muted hover:text-ink"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        {pushOff ? <line x1="3" y1="3" x2="21" y2="21" /> : null}
      </svg>
      {unread > 0 ? (
        <span className="absolute -right-1 -top-1 min-w-[16px] rounded-full bg-hiviz px-1 text-center text-[10px] font-bold leading-4 text-white">
          {unread > 9 ? "9+" : unread}
        </span>
      ) : null}
    </button>
  );
}

/** A small header icon button that signals the shell through a window event. */
function HeaderAction({
  label,
  event,
  children,
}: {
  label: string;
  event: string;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent(event))}
      aria-label={label}
      title={label}
      className="-m-1 p-1 text-muted hover:text-ink"
    >
      {children}
    </button>
  );
}

/**
 * The company logo (public endpoint), falling back to the Tanzeel wordmark if
 * the tenant hasn't uploaded one. `size="lg"` is the centred login treatment.
 */
export function BrandLogo({
  size = "sm",
}: {
  size?: "sm" | "lg";
}): ReactNode {
  const [failed, setFailed] = useState(false);
  if (failed) {
    const box = size === "lg" ? "h-10 w-10" : "h-6 w-6";
    const text = size === "lg" ? "text-lg" : "text-sm";
    return (
      <span className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className={`relative ${box} shrink-0 rounded-md bg-ink`}
        >
          <span
            className="absolute inset-1.5 rounded-[2px] bg-hiviz"
            style={{
              clipPath:
                "polygon(0 55%, 38% 100%, 100% 8%, 82% 0, 36% 62%, 14% 40%)",
            }}
          />
        </span>
        <h1 className={`${text} font-bold uppercase tracking-[0.14em]`}>
          Tanzeel
        </h1>
      </span>
    );
  }
  return (
    <img
      src={`${API_BASE_URL}/public/logo`}
      alt="Company logo"
      className={
        size === "lg"
          ? "h-16 w-auto max-w-[240px] object-contain"
          : "h-8 w-auto max-w-[200px] object-contain"
      }
      onError={() => setFailed(true)}
    />
  );
}

export function AppLayout({
  children,
  signedIn,
  isAdmin = false,
}: {
  children: ReactNode;
  signedIn: boolean;
  isAdmin?: boolean;
}): ReactNode {
  return (
    <div className="flex min-h-full flex-col bg-concrete text-ink">
      {signedIn ? (
        <header className="flex items-center justify-between gap-3 border-b border-line bg-surface px-4 py-3">
          <button
            type="button"
            // Decoupled from the tab state, which lives in AppShell (below this
            // header via the children): the shell listens for this event and
            // resets to the role's default tab.
            onClick={() => window.dispatchEvent(new CustomEvent("tanzeel:home"))}
            className="-m-1 flex min-w-0 items-center gap-2.5 rounded-lg p-1 hover:bg-sunk"
            aria-label="Go to home"
          >
            <BrandLogo />
          </button>
          <div className="flex items-center gap-2.5">
            {isAdmin ? (
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("tanzeel:settings"))
                }
                className="rounded-lg px-2 py-1 text-sm font-semibold text-muted hover:bg-sunk hover:text-ink"
              >
                Settings
              </button>
            ) : null}
            <HeaderAction label="Change password" event="tanzeel:changepw">
              <svg
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="8" cy="15" r="4" />
                <path d="M10.85 12.15 19 4" />
                <path d="m18 5 2 2" />
                <path d="m15 8 2 2" />
              </svg>
            </HeaderAction>
            <HeaderAction label="Sign out" event="tanzeel:signout">
              <svg
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </HeaderAction>
            <HeaderBell />
          </div>
        </header>
      ) : null}
      <main className="flex-1 px-4 py-6">{children}</main>
    </div>
  );
}
